import { Location } from '@angular/common';
import {
  Component,
  computed,
  inject,
  signal,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, map } from 'rxjs';
import { AppRoutes } from '@helpers/app-routes';
import { SvgComponent } from '@components/svg.component';
import { ModalInviteGuestsComponent } from '@components/Modals/modal-invite-guests/modal-invite-guests.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CustomerService } from '@services/customer.service';
import { MealPlanService } from '@services/meal-plan.service';
import { TerminalService } from '@services/terminal.service';
import { UserCompanyDataService } from '@services/user-company-data.service';
import { UserEnterpriseDataService } from '@services/user-enterprise-data.service';
import { InviteOrderService } from '@services/invite-order.service';
import { OrderService } from '@services/order.service';
import { ToastrService } from 'ngx-toastr';
import { HistoryService } from '@services/history.service';
import { FormatPricePipe } from '@pipes/format-price.pipe';

@Component({
  selector: 'app-order-header',
  templateUrl: './order-header.component.html',
  imports: [
    RouterOutlet,
    TranslateModule,
    SvgComponent,
    FormatPricePipe,
    ModalInviteGuestsComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    class: 'flex flex-col h-full',
  },
})
export class OrderHeaderComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly toastr = inject(ToastrService);
  private readonly translate = inject(TranslateService);
  private readonly customerService = inject(CustomerService);
  private readonly mealPlanService = inject(MealPlanService);
  private readonly userEnterpriseDataService = inject(UserEnterpriseDataService);
  private readonly userCompanyDataService = inject(UserCompanyDataService);
  private readonly terminalService = inject(TerminalService);
  private readonly historyService = inject(HistoryService);
  private readonly inviteOrderService = inject(InviteOrderService);
  private readonly orderService = inject(OrderService);

  protected readonly showInviteModal = signal(false);

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );
  private readonly isOnOrderSummary = computed(() =>
    this.currentUrl().includes(AppRoutes.orderSummary),
  );

  protected readonly canInvite = computed(() => this.inviteOrderService.canInvite());
  protected readonly maxInvite = computed(() => this.inviteOrderService.maxInvite());
  protected readonly inviteCount = computed(() => this.inviteOrderService.targetInviteCount());
  protected readonly inviteComment = computed(() => this.inviteOrderService.comment());
  protected readonly totalParticipants = computed(
    () => this.inviteOrderService.participants().length,
  );
  protected readonly currentPersonPosition = computed(
    () => this.inviteOrderService.activeIndex() + 1,
  );
  protected readonly showGuestNav = computed(() => this.totalParticipants() > 1);
  protected readonly canGoPrevious = computed(() => this.inviteOrderService.canGoToPrevious());
  protected readonly canGoNext = computed(() => {
    if (this.inviteOrderService.canGoToNext()) return true;
    if (this.inviteOrderService.isAtLastFilledParticipant()) return !this.isOnOrderSummary();
    if (this.inviteOrderService.isComplete()) return false;

    const activeIndex = this.inviteOrderService.activeIndex();
    const currentSlotFilled = this.inviteOrderService.participants()[activeIndex] !== null;
    return currentSlotFilled || this.orderService.isDraftValid();
  });

  protected readonly customer = computed(() => this.customerService.currentCustomer());
  protected readonly currentMealPlan = computed(() => this.mealPlanService.currentMealPlan());
  protected readonly userEnterpriseData = computed(() =>
    this.userEnterpriseDataService.currentUserEnterpriseData(),
  );
  protected readonly userCompanyData = computed(() =>
    this.userCompanyDataService.currentUserCompanyData(),
  );
  protected readonly paymentTypeKey = computed(() => {
    const type = this.userCompanyData()?.payment_type;
    return type ? `payment_type.${type.toLowerCase()}` : null;
  });

  ngOnInit(): void {
    const customer = this.customer();

    if (!customer) {
      this.#redirectWithError();
      return;
    }

    const enterpriseId = this.terminalService.currentTerminal()?.enterprise_id;

    this.userEnterpriseDataService.getUserEnterpriseData(customer.id).subscribe({
      next: (data) => {
        const hasEnterprise = data.enterprises.some((enterprise) => enterprise.id === enterpriseId);
        if (!hasEnterprise) {
          this.#redirectWithError(
            'order_header.errors.enterprise_not_authorized_message',
            'order_header.errors.enterprise_not_authorized_title',
          );
        }
      },
      error: () => this.#redirectWithError(),
    });

    this.userCompanyDataService.getUserCompanyData(customer.id).subscribe({
      next: (data) => {
        if (!data?.payment_type) {
          this.#redirectWithError();
        }
      },
      error: () => this.#redirectWithError(),
    });
  }

  goBack(): void {
    const current = this.currentUrl();

    const modes = this.terminalService.orderModes();
    const isSingleMode = modes.length === 1;

    if (current === `/${AppRoutes.order}` || current === `/${AppRoutes.chooseMealPlan}`) {
      const isFirstStep =
        isSingleMode && (current === `/${AppRoutes.chooseMealPlan}` || modes[0] === 'by_course');
      if (isFirstStep) {
        this.#goBackFromFirstStep();
      } else if (isSingleMode) {
        void this.router.navigate([AppRoutes.chooseMealPlan]);
      } else {
        void this.router.navigate([AppRoutes.chooseOrderType]);
      }
      return;
    }

    if (current === `/${AppRoutes.chooseOrderType}`) {
      this.#goBackFromFirstStep();
      return;
    }

    this.historyService.back();
  }

  #goBackFromFirstStep(): void {
    if (this.inviteOrderService.canGoToPrevious()) {
      this.goToPreviousGuest();
    } else {
      void this.router.navigate([AppRoutes.scanPage]);
    }
  }

  cancel(): void {
    void this.router.navigate([AppRoutes.scanPage]);
  }

  openInviteModal(): void {
    this.showInviteModal.set(true);
  }

  onInviteCancel(): void {
    this.showInviteModal.set(false);
  }

  onInviteConfirm({ count, comment }: { count: number; comment: string }): void {
    this.showInviteModal.set(false);

    const activeIndexBefore = this.inviteOrderService.activeIndex();
    this.inviteOrderService.setInviteCount(count, comment);
    const activeIndexAfter = this.inviteOrderService.activeIndex();

    if (activeIndexAfter !== activeIndexBefore) {
      const participant = this.inviteOrderService.goToIndex(activeIndexAfter);
      this.inviteOrderService.restoreParticipant(participant);
    }

    if (this.inviteOrderService.participants()[activeIndexAfter] === null) {
      return;
    }

    const pendingIndex = this.inviteOrderService.advanceToNextPending();
    if (pendingIndex !== null) {
      const draft = this.inviteOrderService.drafts()[pendingIndex];
      this.inviteOrderService.restoreParticipant(draft);
      void this.router.navigate([draft ? AppRoutes.order : AppRoutes.chooseOrderType]);
      return;
    }

    if (!this.isOnOrderSummary()) {
      void this.router.navigate([AppRoutes.orderSummary]);
    }
  }

  cancelInvites(event: Event): void {
    event.stopPropagation();
    this.onInviteConfirm({ count: 0, comment: '' });
  }

  goToPreviousGuest(): void {
    if (this.isOnOrderSummary()) {
      const activeIndex = this.inviteOrderService.activeIndex();
      this.inviteOrderService.restoreParticipant(this.inviteOrderService.goToIndex(activeIndex));
      void this.router.navigate([AppRoutes.order]);
      return;
    }

    this.#formallySubmitIfValid();
    this.inviteOrderService.restoreParticipant(this.inviteOrderService.goToPrevious());
    void this.router.navigate([AppRoutes.order]);
  }

  goToNextGuest(): void {
    this.#formallySubmitIfValid();

    if (this.inviteOrderService.canGoToNext()) {
      this.inviteOrderService.restoreParticipant(this.inviteOrderService.goToNext());
      void this.router.navigate([AppRoutes.order]);
      return;
    }

    if (this.inviteOrderService.isAtLastFilledParticipant() && !this.isOnOrderSummary()) {
      void this.router.navigate([AppRoutes.orderSummary]);
      return;
    }

    const activeIndex = this.inviteOrderService.activeIndex();
    const pendingIndex = this.inviteOrderService.advanceToNextPending();
    if (pendingIndex !== null && pendingIndex !== activeIndex) {
      const draft = this.inviteOrderService.drafts()[pendingIndex];
      this.inviteOrderService.restoreParticipant(draft);
      void this.router.navigate([draft ? AppRoutes.order : AppRoutes.chooseOrderType]);
    }
  }

  #formallySubmitIfValid(): void {
    const draft = this.orderService.currentOrder();
    if (!draft || !this.orderService.isDraftValid()) return;
    this.inviteOrderService.submitCurrentCart(draft, this.mealPlanService.currentMealPlan());
  }

  protected getBalanceColor(): string {
    const currency = this.customer()?.wallet?.currency;
    if (currency && currency > 0) return 'fg-vert-1';
    if (currency && currency < 0) return 'fg-rose-1';
    return 'fg-gris-1';
  }

  #redirectWithError(
    messageKey = 'order_header.errors.fetch_client_message',
    titleKey = 'order_header.errors.fetch_client_title',
  ): void {
    this.toastr.error(this.translate.instant(messageKey), this.translate.instant(titleKey));
    void this.router.navigate([AppRoutes.scanPage]);
  }
}
